import { type MenuElement } from './types';
import { useMemo } from 'react';
import { useFeatureFlags } from '@Contexts/featureFlags';
import { settingsMenuElements, profileMenuElements } from '@Constants/navigationConstants';
import { EnumNavigationBar } from '@Contexts/navigation';
import { useIsPortalSession } from '@Contexts/session';
import { useMainMenuElements } from '@Hooks/Navigation/useMainMenuElements';
import { usePortalMenuElements } from '@Hooks/Navigation/usePortalMenuElements';

type IsFeatureEnabled = (featureName: keyof ReturnType<typeof useFeatureFlags>['featureFlags']) => boolean;

function hasAllPermissions(required: string[], granted: string[]): boolean {
    return required.every((permission) => granted.includes(permission));
}

// @ai-generated 2026-07-21T06:16:43Z by malte.ulfers@tngtech.com using Claude Opus 4.7 (1M context)
function filterMenuElementsByPermissions(
    menuElements: MenuElement[],
    permissions: string[],
    isFeatureEnabled: IsFeatureEnabled,
): MenuElement[] {
    const filtered: MenuElement[] = [];

    for (const element of menuElements) {
        if (element.kind === 'leaf') {
            const featureFlagEnabled = element.featureFlag ? isFeatureEnabled(element.featureFlag) : true;
            if (hasAllPermissions(element.permissions, permissions) && featureFlagEnabled) {
                filtered.push(element);
            }
            continue;
        }

        const targets = element.targets.filter((target) => {
            const featureFlagEnabled = target.featureFlag ? isFeatureEnabled(target.featureFlag) : true;
            return hasAllPermissions(target.permissions, permissions) && featureFlagEnabled;
        });
        if (targets.length > 0) {
            filtered.push({ ...element, targets });
        }
    }

    return filtered;
}

export function useMenuFiltering(navigationBar: EnumNavigationBar, permissions: string[]): MenuElement[] {
    const { isFeatureEnabled } = useFeatureFlags();
    const mainMenuElements = useMainMenuElements();
    const portalMenuElements = usePortalMenuElements();
    const isPortalSession = useIsPortalSession();

    return useMemo(() => {
        let menuElements: MenuElement[];

        switch (navigationBar) {
            case EnumNavigationBar.Main:
                menuElements = isPortalSession ? portalMenuElements : mainMenuElements;
                break;
            case EnumNavigationBar.Settings:
                menuElements = settingsMenuElements;
                break;
            case EnumNavigationBar.Profile:
                menuElements = profileMenuElements;
                break;
            default:
                return [];
        }

        return filterMenuElementsByPermissions(menuElements, permissions, isFeatureEnabled);
    }, [navigationBar, permissions, isFeatureEnabled, mainMenuElements, portalMenuElements, isPortalSession]);
}
